/* eslint-disable indent */
/* eslint-disable react/jsx-indent */
/* eslint-disable camelcase */
import React, { useEffect } from 'react'
import Box from '@mui/material/Box'
import Snackbar from '@mui/material/Snackbar'
import MuiAlert from '@mui/material/Alert'
import Divider from '@mui/material/Divider'
import { useDispatch, useSelector } from 'react-redux'
import StreetmixApp from '../../../app/StreetmixApp'
import { setPedestrianSegmentInput } from '../../utils/pedestrianSegmentInput.utils'
import { setBikeSegmentInput } from '../../utils/bikeSegmentInput.utils'
import { setTransitSegmentInput } from '../../utils/transitSegmentInput.utils'
import { setFreightSegmentInput } from '../../utils/frieghtSegmentInput.utils'
import {
  setCriteriaElmScrollTop,
  setShareableIntroModalShownOnce,
  setShowCopyLinkModal,
  setShowStreetmixIntroModal,
  setShowStreetmixToaster
} from '../../slices/appSlice'
import { calculatePedestrianScore } from '../../utils/pedestrianScore'
import { calculateBikeScore } from '../../utils/bikeScore'
import { calculateAutoScore } from '../../utils/autoScore'
import { calculateFreightScore } from '../../utils/freightScore'
import { calculateTransitScore } from '../../utils/transitScore'
import calculateFinalScores from '../../utils/calculateFinalScores.utils'
import {
  CURB_LANE_WIDTH,
  SPECIAL_CONSIDERATIONS
} from '../../constants/freight'
import calculateWeakestLinkScore from '../../utils/calculateWeakestLinkScore.utils'
import CustomStreetmixTour from '../CustomStreetmixTour'
import CustomModal from '../CustomModal'
import { themeConfig } from '../../config/theme.config'
import { saveStreetToServer } from '../../../streets/xhr'
import {
  CROSSING_IMPROVEMENTS,
  LIGHTING,
  PEDESTRIAN_FACILITY_TYPE
} from '../../constants/pedestrian'
import { setPedestrianStreetmixInput } from '../../utils/pedestrianStreetmixInput.utils'
import {
  BIKE_FACILITY_TYPE,
  SHARED_LANE_PAVEMENT_MARKINGS_PRESENT
} from '../../constants/bike'
import { setbikeStreetmixInput } from '../../utils/bikeStreetmixInput.utils'
import { resetPedestrianInputs } from '../../slices/criteriaSelection/pedestrian'
import { resetBicycleInputs } from '../../slices/criteriaSelection/bicycle'
import { resetFreightInputs } from '../../slices/criteriaSelection/freight'
import { setFreightStreetmixInput } from '../../utils/freightStreetmixInput.utils'
import { resetTransitInputs } from '../../slices/criteriaSelection/transit'
import {
  HIGH_CAPACITY_TRANSIT_FACILITY,
  TRANSIT_STOP_AMENITIES
} from '../../constants/transit'
import { setTransitStreetmixInput } from '../../utils/transitStreetmixInput.utils'
import { resetAutoInputs } from '../../slices/criteriaSelection/auto'
import CriteriaAccordion from './CriteriaAccordion'
import Scoring from './Scoring'

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />
})

export default function CriteriaSelection() {
  const { segments } = useSelector((state) => state.street)
  const [snackOpen, setSnackOpen] = React.useState(false)

  const {
    showStreetmixToaster,
    shareableURL,
    showCopyLinkModal,
    showStreetmixIntroModal,
    shareableIntroModalShownOnce,
    shareableURLSearchParam
  } = useSelector((state) => state.appSlice)
  const { pedestrian, bicycle, auto, transit, freight } = useSelector(
    (state) => state
  )
  const {
    Pedestrian: PTier,
    Bicycle: BTier,
    Freight: FTier,
    Transit: TTier,
    Auto: ATier
  } = useSelector((state) => state.modalTiers)
  const dispatch = useDispatch()
  const {
    pedestrian_facility_type,
    comfort_zone_width,
    sidewalk_accessibility,
    lighting,
    heavy_vehicles,
    crossing_spacing,
    special_considerations,
    crossing_improvements,
    scores: pedestrianScores
  } = pedestrian

  const {
    bike_facility_type,
    shared_lane_pavement_markings_present,
    lateral_distance_between_markings_and_edge_of_sidewalk_or_parked_cars,
    mid_block_conflicts,
    scores: bikeScores
  } = bicycle

  const { transit_stop_amenities, scores: transitScores } = transit

  const { vc, scores: autoScores } = auto

  const {
    curb_lane_width,
    special_considerations: special_considerations_freight,
    scores: freightScores
  } = freight

  useEffect(() => {
    if (!shareableURL) {
      // if the respectibe tier is not 0, then set the segment inputs
      PTier !== 0 && setPedestrianSegmentInput()
      BTier !== 0 && setBikeSegmentInput()
      TTier !== 0 && setTransitSegmentInput()
      FTier !== 0 && setFreightSegmentInput()
    }
  }, [segments, PTier, BTier, FTier, TTier])

  useEffect(() => {
    // When pedestrian criteria changes, recalculate pedestrian score
    !shareableURL && calculatePedestrianScore('', true)
  }, [
    pedestrian_facility_type,
    comfort_zone_width,
    sidewalk_accessibility,
    lighting,
    heavy_vehicles,
    crossing_spacing,
    special_considerations,
    crossing_improvements
  ])

  useEffect(() => {
    calculateFinalScores('pedestrian', pedestrianScores)
    calculateWeakestLinkScore('pedestrian', pedestrianScores)
  }, [pedestrianScores])

  useEffect(() => {
    // When bicycle criteria changes, recalculate pedestrian score
    !shareableURL && calculateBikeScore('', true)
  }, [
    bike_facility_type,
    shared_lane_pavement_markings_present,
    lateral_distance_between_markings_and_edge_of_sidewalk_or_parked_cars,
    mid_block_conflicts
  ])

  useEffect(() => {
    calculateFinalScores('bicycle', bikeScores)
    calculateWeakestLinkScore('bicycle', bikeScores)
  }, [bikeScores])

  useEffect(() => {
    // When transit criteria changes, recalculate pedestrian score
    !shareableURL && calculateTransitScore()
  }, [transit_stop_amenities])

  useEffect(() => {
    calculateFinalScores('transit', transitScores)
    calculateWeakestLinkScore('transit', transitScores)
  }, [transitScores])

  useEffect(() => {
    // When auto criteria changes, recalculate pedestrian score
    !shareableURL && calculateAutoScore()
  }, [vc])

  useEffect(() => {
    calculateFinalScores('auto', autoScores)
    calculateWeakestLinkScore('auto', autoScores)
  }, [autoScores])

  useEffect(() => {
    // When freight criteria changes, recalculate pedestrian score
    !shareableURL && calculateFreightScore(CURB_LANE_WIDTH)
    !shareableURL && calculateFreightScore(SPECIAL_CONSIDERATIONS)
  }, [curb_lane_width, special_considerations_freight])

  useEffect(() => {
    calculateFinalScores('freight', freightScores)
    calculateWeakestLinkScore('freight', freightScores)
  }, [freightScores])

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return
    }

    dispatch(setShowStreetmixToaster(false))
  }

  useEffect(() => {
    // When Ptier is NA/0 -> pedestrian facility type is No sidewalk, no lighting, no median
    if (PTier === 0) {
      dispatch(resetPedestrianInputs())

      //  setPedestrianStreetmixInput(PEDESTRIAN_FACILITY_TYPE)
      //   setPedestrianStreetmixInput(LIGHTING)
      //   setPedestrianStreetmixInput(CROSSING_IMPROVEMENTS)

      // Set All scores to NA
      calculatePedestrianScore('', true)
    }
  }, [PTier])

  useEffect(() => {
    // When BTier is NA/0 -> Bike facility type is No bikeway, set shared lane to NO
    if (BTier === 0) {
      dispatch(resetBicycleInputs())

      // setbikeStreetmixInput(BIKE_FACILITY_TYPE)
      //  setbikeStreetmixInput(SHARED_LANE_PAVEMENT_MARKINGS_PRESENT)

      // Set All scores to NA
      calculateBikeScore('', true)
    }
  }, [BTier])

  useEffect(() => {
    // When FTier is NA/0 -> reset all freight inputs
    if (FTier === 0) {
      dispatch(resetFreightInputs())

      // setFreightStreetmixInput(CURB_LANE_WIDTH)

      // Set All scores to NA
      calculateFreightScore('')
    }
  }, [FTier])

  useEffect(() => {
    // When TTier is NA/0 -> reset all transit inputs
    if (TTier === 0) {
      dispatch(resetTransitInputs())
      // setTransitStreetmixInput(TRANSIT_STOP_AMENITIES)
      // setTransitStreetmixInput(HIGH_CAPACITY_TRANSIT_FACILITY)

      // Set All scores to NA
      calculateTransitScore()
    }
  }, [TTier])

  useEffect(() => {
    // When TTier is NA/0 -> reset all transit inputs
    if (ATier === 0) {
      dispatch(resetAutoInputs())

      // Set All scores to NA
      calculateAutoScore()
    }
  }, [ATier])

  const handleCopyToClipboard = () => {
    setSnackOpen(true)
    const mainURL = !window.location.toString().includes('/shareable')
      ? window.location.toString() + '/shareable'
      : window.location.toString()
    navigator.clipboard.writeText(
      mainURL + '?prj_name=' + shareableURLSearchParam
    )
    saveStreetToServer(false, true)
    dispatch(setShowCopyLinkModal(false))
  }

  const handleShareableIntroModal = () => {
    dispatch(setShareableIntroModalShownOnce(true))
  }

  return (
    <div
      style={{ flexGrow: 1, overflow: 'scroll' }}
      onScroll={() => {
        const elm = document.getElementById('criteriaSelection')
        dispatch(setCriteriaElmScrollTop(elm.scrollTop))
      }}
      id="criteriaSelection"
    >
      {showCopyLinkModal && (
        <CustomModal
          title="Shareable Link"
          btnText="COPY TO CLIPBOARD"
          handleBtnClick={handleCopyToClipboard}
          modalContent=" You can share your customized street data with others and receive
          feedback. Keep in mind that users who access the link will only have
          viewing privileges for your street. Any modifications they make will
          not be saved and will generate a completely new street."
        />
      )}
      {shareableURL && !shareableIntroModalShownOnce && (
        <CustomModal
          title="Welcome to Multimodal Tool!"
          btnText="View Street"
          handleBtnClick={handleShareableIntroModal}
          showTakeTour={true}
          modalContent="As you explore this pre-configured street, remember that any modifications or edits you make will result in a new project. To design your unique street, simply click 'New street' on the menubar."
        />
      )}
      {showStreetmixIntroModal && !shareableURL && (
        <CustomModal
          title="Start Building Your Project"
          btnText="Configure Street"
          handleBtnClick={() => {
            dispatch(setShowStreetmixIntroModal(false))
          }}
          showDoNotShowAgain={true}
          showTakeTour={true}
          modalContent="Envision your project by dragging, dropping, and adjusting the different street segments in the cross section. In the panel at the bottom, enter additional details about your project to understand how well it serves each user. The tool will score each item based on your configuration, with 1 representing the best configuration and 4 representing an opportunity for improvement."
        />
      )}
      <Snackbar
        message="Shareable link copied to clipboard !"
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={2000}
        onClose={() => setSnackOpen(false)}
        open={snackOpen}
        sx={{
          top: '70px',
          backgroundColor: themeConfig.semcogGreen + ' !important',
          color: 'white',
          '& .MuiSnackbarContent-root': {
            position: 'absolute',
            top: 100,
            background: themeConfig.semcogGreen + ' !important'
          }
        }}
      />
      <CustomStreetmixTour />
      <StreetmixApp />
      <Box
        sx={{
          flexGrow: 1,
          p: '2em'
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            gridTemplateColumns: '50% 1% 49%',
            display: 'grid'
          }}
        >
          <Snackbar
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            open={showStreetmixToaster}
            autoHideDuration={2000}
            onClose={handleClose}
          >
            <Alert
              onClose={handleClose}
              severity="success"
              sx={{
                mb: 2,
                width: '30vw',
                position: 'absolute',
                color: 'white',
                top: 70,
                backgroundColor: themeConfig.semcogGreen,
                zIndex: 1050,
                '& .MuiAlert-message': {
                  alignSelf: 'center'
                },
                '& .MuiAlert-icon': {
                  color: 'white !important'
                }
              }}
            >
              Your Streetmix has been updated!
            </Alert>
          </Snackbar>
          <CriteriaAccordion />
          <Divider orientation="vertical" />
          <Scoring />
        </Box>
      </Box>
    </div>
  )
}
